import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { ImagePlus, Loader2, Trash2 } from 'lucide-react';
import { api } from '../../services/api.js';
import { extractApiError } from '../../utils/pdfDownload';

const MAX_BYTES = 3 * 1024 * 1024;
const ACCEPTED = 'image/png,image/jpeg,image/webp';

interface VariantImageUploadProps {
  variantId: string;
  /** Stored path, null when no packshot has been uploaded yet. */
  imagePath: string | null;
  onChange: (imagePath: string | null) => void;
}

/**
 * Packshot of a single variant, shown in follow-up emails.
 *
 * The image is fetched with the JWT and turned into a blob URL because an `img` tag
 * cannot send an Authorization header — same approach as UserAvatar.
 */
export const VariantImageUpload: React.FC<VariantImageUploadProps> = ({
  variantId,
  imagePath,
  onChange,
}) => {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;

    const load = async () => {
      if (!imagePath) {
        setBlobUrl(null);
        return;
      }
      try {
        const res = await api.get(`/products/variants/${variantId}/image`, {
          responseType: 'blob',
        });
        if (cancelled) return;
        objectUrl = URL.createObjectURL(res.data);
        setBlobUrl(objectUrl);
      } catch {
        if (!cancelled) setBlobUrl(null);
      }
    };

    void load();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [variantId, imagePath]);

  const handleSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    if (file.size > MAX_BYTES) {
      toast.error('Image too large (max 3 MB)');
      return;
    }

    setBusy(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await api.post(
        `/products/variants/${variantId}/image`,
        form,
        {
          transformRequest: [
            (data, headers) => {
              // Let the browser set the multipart boundary.
              if (headers && typeof headers === 'object') {
                delete (headers as Record<string, unknown>)['Content-Type'];
              }
              return data;
            },
          ],
        }
      );
      onChange(res.data.image_path ?? null);
      toast.success('Packshot uploaded');
    } catch (err) {
      const { message } = extractApiError(err);
      toast.error(`Upload failed: ${message}`);
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async () => {
    setBusy(true);
    try {
      await api.delete(`/products/variants/${variantId}/image`);
      onChange(null);
      toast.success('Packshot removed');
    } catch (err) {
      const { message } = extractApiError(err);
      toast.error(`Failed to remove packshot: ${message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-3">
      <div
        className="w-14 h-14 shrink-0 rounded-xl border-2 border-dashed border-gray-200
          bg-white flex items-center justify-center overflow-hidden"
      >
        {blobUrl ? (
          <img src={blobUrl} alt="Packshot" className="w-full h-full object-contain" />
        ) : (
          <ImagePlus size={18} className="text-gray-300" />
        )}
      </div>
      <div className="min-w-0">
        <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">
          Packshot
        </p>
        <p className="text-xs text-gray-400 mb-1">PNG, JPEG or WebP · max 3 MB</p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={() => inputRef.current?.click()}
            className="flex items-center gap-1 text-xs font-bold text-brand-600
              hover:text-brand-700 disabled:opacity-50"
          >
            {busy ? <Loader2 size={13} className="animate-spin" /> : <ImagePlus size={13} />}
            {imagePath ? 'Replace' : 'Upload'}
          </button>
          {imagePath && (
            <button
              type="button"
              disabled={busy}
              onClick={() => void handleRemove()}
              className="flex items-center gap-1 text-xs font-bold text-gray-400
                hover:text-red-600 disabled:opacity-50"
            >
              <Trash2 size={13} />
              Remove
            </button>
          )}
        </div>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED}
        onChange={(e) => void handleSelect(e)}
        className="hidden"
      />
    </div>
  );
};
